import React from 'react';
import { useSelector } from 'react-redux';
import { Modal } from '../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { selectCurrentClient } from '../CommonSelectors/CommonAreaSelector';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { ModalFooter } from '../CommonComponents/OPL/Modal/ModalFooter';
import { useDeleteClientMutation } from './api/useDeleteClientMutation';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../CommonComponents/OPL/Alert/Alert';
import { toast } from '../CommonComponents/OPL/Toast/Toast';

interface Props {
  clientId: string;
  onClose(): void;
}

export const DeleteClientModal = ({ clientId, onClose }: Props) => {
  const currentClient = useSelector(selectCurrentClient);

  const canDeleteClient = currentClient?.CanDeleteConsumer;

  const {
    mutateAsync: deleteClient,
    isLoading: isDeletingClient,
    error: deleteClientError,
  } = useDeleteClientMutation();

  const onDeleteClient = async () => {
    try {
      await deleteClient({ clientId, canDeleteClient });

      toast.success(
        canDeleteClient ? 'Client deleted successfully.' : 'Client removed successfully.',
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      aria={{ labelledby: 'delete-client-modal--heading' }}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader as="h2" id="delete-client-modal--heading">
        Are you sure you want to {canDeleteClient ? 'delete' : 'remove'} {currentClient.FirstName}{' '}
        {currentClient.LastName}?
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      {deleteClientError && (
        <ModalBody>
          <Alert message={deleteClientError.message} variant="error" />
        </ModalBody>
      )}

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button variant="error" isLoading={isDeletingClient} onClick={onDeleteClient} ml="12">
          {canDeleteClient ? 'Delete' : 'Remove'} This Client
        </Button>
      </ModalFooter>
    </Modal>
  );
};
